import { HttpParams } from '@angular/common/http'
import { NavigationStart, Event as Event_2, ParamMap } from '@angular/router'
import {
  CardModel,
  ContainerModel,
  ContainerPartial,
  EntityHubLayout,
  FilterableContainer,
  FilterableGrid,
  FilterableGridPartial,
  FilterablePartial,
  GridContainer,
  GridContainerPartial,
  LinkModel,
  ListScreenViewModel,
  MobileNavigationListPartial,
  MobileNavigationViewModel,
  MultipleSectionsViewModel,
  ViewComponent
} from '@flocasts/experience-service-types'
import { EXPERIENCE_API_ENTITY_HUB_BASE_PATH, EXPERIENCE_API_VERSION } from '../app.config'
import {
  isContainerModel,
  isContainerPartial,
  isEntityHubLayout,
  isFilterableContainer,
  isFilterableGridContainer,
  isFilterableGridPartial,
  isFilterablePartial,
  isGridContainer,
  isGridContainerPartial,
  isPaginationLink
} from '../shared/functions/type-guards'
import { getIdFromSlug } from '../shared/utility-functions/slug.utility'

function getPaginationLink(
  container:
    | ContainerModel
    | GridContainer
    | FilterableContainer
    | FilterableGrid
    | ListScreenViewModel
    | MultipleSectionsViewModel
    | MobileNavigationListPartial
    | null
): string | null {
  if ((!!container && isContainerModel(container)) || isGridContainer(container)) {
    const link = container.items.find(item => isPaginationLink(item)) as LinkModel
    return link?.url
  }
  if (isFilterableContainer(container) || isFilterableGridContainer(container)) {
    const link = container.content?.items?.find(item => isPaginationLink(item)) as LinkModel
    return link?.url
  }

  return null
}

export function paginationLinksMatch(
  prev:
    | ContainerModel
    | GridContainer
    | FilterableContainer
    | FilterableGrid
    | ListScreenViewModel
    | MultipleSectionsViewModel
    | MobileNavigationListPartial
    | null,
  curr:
    | ContainerModel
    | GridContainer
    | FilterableContainer
    | FilterableGrid
    | ListScreenViewModel
    | MultipleSectionsViewModel
    | MobileNavigationListPartial
    | null
): boolean {
  if (!prev || !curr) return false
  const prevLink = getPaginationLink(prev)
  const currLink = getPaginationLink(curr)

  return (
    !!prevLink &&
    prevLink !== currLink &&
    (!!currLink
      ? prevLink.slice(0, prevLink.indexOf('/partial')) === currLink.slice(0, currLink.indexOf('/partial'))
      : !!prevLink.slice(0, prevLink.indexOf('/partial')))
  )
}

export function somethingInTheRouteChanged(
  oldQueryParams: ParamMap,
  queryParams: ParamMap,
  oldChildParams: ParamMap,
  childParams: ParamMap,
  oldParams: ParamMap,
  params: ParamMap
): boolean {
  return (
    oldQueryParams.get('search') !== queryParams.get('search') ||
    oldQueryParams.get('coreNodeId') !== queryParams.get('coreNodeId') ||
    oldQueryParams.get('facets') !== queryParams.get('facets') ||
    oldQueryParams.get('offset') !== queryParams.get('offset') ||
    oldQueryParams.get('date') !== queryParams.get('date') ||
    oldChildParams.get('tab') !== childParams.get('tab') ||
    oldParams.get('nodeId') !== params.get('nodeId') ||
    oldParams.get('collectionId') !== params.get('collectionId') ||
    oldParams.get('collectionTag') !== params.get('collectionTag')
  )
}

/**
 * Vanity urls have singular tabs, but BFF endpoints have different naming convention.
 * @param tab The vanity url tab name.
 * @returns The BFF endpoint tab param.
 */
export function getEntityHubTab(tab?: string | null) {
  if (!tab) return null
  switch (tab) {
    case 'event':
      return 'schedule'
    case 'video':
      return 'videos'
    case 'article':
      return 'articles'
    case 'ranking':
      return 'rankings'
    case 'result':
      return 'results'
    default:
      return tab
  }
}

export function getId(idWithSlug: string | null) {
  if (!!idWithSlug) {
    return getIdFromSlug(idWithSlug)
  }
  return null
}

export function handleBrowserBackButton(e: Event_2, tabs: LinkModel[]): LinkModel | undefined {
  if (e instanceof NavigationStart) {
    // Update tab state when user hits browser back/forward actions
    if (e.navigationTrigger === 'popstate') {
      const selectedTab = tabs?.find(tab => (!!tab.url && e.url.includes(tab.url)) || tab.url === e.url)
      if (!!selectedTab) {
        // Update which nav item/tab is selected
        return selectedTab
      } else if (!!tabs) {
        // We're on the default tab if the urls don't match up.
        return tabs[0]
      }
    }
  }

  return
}

export function appendPaginatedItems(
  acc: FilterablePartial | FilterableGridPartial | GridContainerPartial | ContainerPartial | undefined,
  curr: FilterablePartial | FilterableGridPartial | GridContainerPartial | ContainerPartial | undefined,
  entityHubResponse:
    | EntityHubLayout
    | FilterablePartial
    | FilterableGridPartial
    | GridContainerPartial
    | MultipleSectionsViewModel
    | ContainerPartial
    | undefined
):
  | FilterablePartial
  | EntityHubLayout
  | FilterableGridPartial
  | GridContainerPartial
  | MultipleSectionsViewModel
  | ContainerPartial
  | undefined {
  /**
   * If no paging call, return the accumulated pagination items because
   * we are now at the end of infinite scroll.
   *  - or -
   * If no paging call, return the unmutated response because there are no items
   * to page.
   */
  if (!curr) {
    return entityHubResponse
  }

  /**
   * If current response is a partial AND it contains a pagination link,
   * we need to append items as user infinite scrolls.
   */
  // If previous value was a partial, combine container items
  if (
    isFilterablePartial(acc) &&
    isFilterablePartial(curr) &&
    !!curr.data.content &&
    paginationLinksMatch(acc.data.content, curr.data.content)
  ) {
    return combineFilterablePartialItems(acc, curr)
  }

  if (
    isFilterableGridPartial(acc) &&
    isFilterableGridPartial(curr) &&
    !!curr.data.content &&
    paginationLinksMatch(acc.data.content, curr.data.content)
  ) {
    return combineFilterableGridPartialItems(acc, curr)
  }

  if (isGridContainerPartial(acc) && isGridContainerPartial(curr) && paginationLinksMatch(acc.data, curr.data)) {
    return combineGridPartialItems(acc, curr)
  }

  if (isContainerPartial(acc) && isContainerPartial(curr) && paginationLinksMatch(acc.data, curr.data)) {
    return combineContainerPartialItems(acc, curr)
  }

  // If last value of the main stream was a layout, combine layout & partial container items
  if (!!entityHubResponse && isEntityHubLayout(entityHubResponse) && !!entityHubResponse.container) {
    if (isFilterablePartial(curr) && paginationLinksMatch(entityHubResponse.container, curr.data.content)) {
      return combineLayoutItemsIntoFilterablePartial(entityHubResponse, curr)
    }
    if (isFilterableGridPartial(curr) && paginationLinksMatch(entityHubResponse.container, curr.data.content)) {
      return combineLayoutItemsIntoFilterableGridPartial(entityHubResponse, curr)
    }
    if (isGridContainerPartial(curr) && paginationLinksMatch(entityHubResponse.container, curr.data)) {
      return combineLayoutItemsIntoGridPartial(entityHubResponse, curr)
    }
    if (isContainerPartial(curr) && paginationLinksMatch(entityHubResponse.container, curr.data)) {
      return combineLayoutItemsIntoContainerPartial(entityHubResponse, curr)
    }
  }

  return curr
}

function removePaginationLink(
  container:
    | FilterableContainer
    | FilterableGrid
    | ContainerModel
    | GridContainer
    | ListScreenViewModel
    | MultipleSectionsViewModel
    | MobileNavigationListPartial
    | null
): ViewComponent[] {
  if (isFilterableContainer(container) || isFilterableGridContainer(container)) {
    return container.content?.items?.filter(item => !isPaginationLink(item)) || []
  }

  if (isContainerModel(container) || isGridContainer(container)) {
    return container.items?.filter(item => !isPaginationLink(item)) || []
  }

  return []
}

function combineLayoutItemsIntoFilterablePartial(
  entityHubLayout: EntityHubLayout,
  curr: FilterablePartial
): FilterablePartial | undefined {
  if (!curr.data.content) return
  return {
    ...curr,
    data: {
      ...curr.data,
      content: {
        ...curr.data.content,
        items: [
          // Remove the previous pagination link so we don't get dupe API calls
          ...removePaginationLink(entityHubLayout.container),
          ...(curr.data.content?.items || [])
        ]
      }
    }
  }
}

function combineLayoutItemsIntoFilterableGridPartial(
  entityHubLayout: EntityHubLayout,
  curr: FilterableGridPartial
): FilterableGridPartial | undefined {
  if (!curr.data.content) return
  return {
    ...curr,
    data: {
      ...curr.data,
      content: {
        ...curr.data.content,
        items: [
          // Remove the previous pagination link so we don't get dupe API calls
          ...(removePaginationLink(entityHubLayout.container) as CardModel[]),
          ...(curr.data.content?.items || [])
        ]
      }
    }
  }
}

function combineLayoutItemsIntoGridPartial(
  entityHubLayout: EntityHubLayout,
  curr: GridContainerPartial
): GridContainerPartial | undefined {
  return {
    ...curr,
    data: {
      ...curr.data,
      items: [
        // Remove the previous pagination link so we don't get dupe API calls
        ...(removePaginationLink(entityHubLayout.container) as CardModel[]),
        ...(curr.data.items || [])
      ]
    }
  }
}

function combineLayoutItemsIntoContainerPartial(
  entityHubLayout: EntityHubLayout,
  curr: ContainerPartial
): ContainerPartial | undefined {
  return {
    ...curr,
    data: {
      ...curr.data,
      items: [
        // Remove the previous pagination link so we don't get dupe API calls
        ...(removePaginationLink(entityHubLayout.container) as CardModel[]),
        ...(curr.data.items || [])
      ]
    }
  }
}

function combineFilterablePartialItems(acc: FilterablePartial, curr: FilterablePartial): FilterablePartial | undefined {
  if (!curr.data.content) return
  return {
    ...curr,
    data: {
      ...curr.data,
      content: {
        ...curr.data.content,
        items: [
          // Remove the previous pagination link so we don't get dupe API calls
          ...(acc.data.content?.items?.filter(item => !isPaginationLink(item)) || []),
          ...(curr.data.content?.items || [])
        ]
      }
    }
  }
}

function combineFilterableGridPartialItems(
  acc: FilterableGridPartial,
  curr: FilterableGridPartial
): FilterableGridPartial | undefined {
  if (!curr.data.content) return
  return {
    ...curr,
    data: {
      ...curr.data,
      content: {
        ...curr.data.content,
        items: [
          // Remove the previous pagination link so we don't get dupe API calls
          ...(acc.data.content?.items?.filter(item => !isPaginationLink(item)) || []),
          ...(curr.data.content?.items || [])
        ]
      }
    }
  }
}

function combineGridPartialItems(acc: GridContainerPartial, curr: GridContainerPartial): GridContainerPartial {
  return {
    ...curr,
    data: {
      ...curr.data,
      items: [
        // Remove the previous pagination link so we don't get dupe API calls
        ...(removePaginationLink(acc.data) as CardModel[]),
        ...(curr.data.items || [])
      ]
    }
  }
}

function combineContainerPartialItems(acc: ContainerPartial, curr: ContainerPartial): ContainerPartial {
  return {
    ...curr,
    data: {
      ...curr.data,
      items: [
        // Remove the previous pagination link so we don't get dupe API calls
        ...(removePaginationLink(acc.data) as CardModel[]),
        ...(curr.data.items || [])
      ]
    }
  }
}

/*
 * Return HttpParams common to all entity hub GET requests
 */
export function getEntityHubHttpParams(
  queryParams: ParamMap | null,
  useCoreApiTabs?: boolean,
  showLeagueEvents?: boolean,
  useCalendarLayoutVersion?: boolean,
  isNextGenEventHub?: boolean
): HttpParams {
  let params = new HttpParams({
    fromObject: {
      version: useCalendarLayoutVersion ? '1.36.0' : EXPERIENCE_API_VERSION,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
      ...(useCoreApiTabs && { useCoreApi: useCoreApiTabs }),
      ...(showLeagueEvents && { showVarsityLeagueData: showLeagueEvents })
    }
  })
  const facets = queryParams?.get('facets')
  params = facets ? params.set('facets', facets) : params
  const offset = queryParams?.get('offset')
  params = offset ? params.set('offset', offset) : params
  const viewMode = queryParams?.get('viewMode')
  params = viewMode ? params.set('viewMode', viewMode) : params
  const date = queryParams?.get('date')
  params = date ? params.set('date', date) : params
  params = params.set('isNextGenEventHub', isNextGenEventHub ?? 'false')
  return params
}

export function getEntityHubLayoutEndpoint(id: number, tab: string | null | undefined) {
  return `${EXPERIENCE_API_ENTITY_HUB_BASE_PATH}/${id}${!!tab ? `/${tab}` : ''}`
}

/**
 * This function is used to determine if the user is on the schedule tab,
 * based on the array of tabs in the entity hub subnavigation.
 */
export function userIsOnScheduleTab(tabs: LinkModel[]): boolean {
  return tabs.some(tab => tab.id.includes('schedule') && tab.state === 'selected')
}

export function userIsOnNewScheduleTab(tabs: LinkModel[]): boolean {
  return tabs.some(tab => tab.id.includes('events') && tab.state === 'selected')
}

export function isTeamEntityType(routerUrl: string): boolean {
  return (
    routerUrl.startsWith('/teams/') ||
    routerUrl.startsWith('/leagues/') ||
    routerUrl.startsWith('/conferences/') ||
    routerUrl.startsWith('/sports/') ||
    routerUrl.startsWith('/schools/')
  )
}
